/**
 * 공개 견적 요청(마켓플레이스) API — 공급사(trp) 측.
 *
 * 대리점(hanmaeum)이 수배처를 지정하지 않고 올린 공개 견적 요청을 조회하고, 공급사가
 * "참여"하면 그 시점에 기존 SP(DBO.ITN_T_상담저장)로 진짜 상담(수배처=자기)을
 * 생성해 상담관리(/quotes)로 이관한다. 동시에 estimate_bids 응찰 행을 쌓고,
 * 대리점이 실어 보낸 일정 스마트 등록 JSON 을 estimate_extras(상담번호 키)로 복사해
 * 상담 저장 시 상품+일정표 생성에 재사용하게 한다.
 *
 * ⚠ 수배처번호 라우팅: 기존 flow 는 @업체번호=대리점, 수배처번호=공급사로 저장하면
 *    공급사의 ITN_S_상담리스트(@업체번호=공급사)가 이를 받는다. 여기서도 동일하게
 *    @업체번호=요청.agency_no, 수배처번호=내 업체번호로 저장한다. SP 내부 매칭 동작은
 *    라이브 검증 필요(공급사 토큰으로 대리점 @업체번호 저장이 허용되는지 확인).
 */
import { supabase } from '../lib/supabaseClient';
import { getAuthHeader, getUserInfo } from '../utils/tokenManager';
import { getConsultList } from './apiService';
import { isDemoMode } from '../demo/demoMode';

/**
 * 데모 모드 차단.
 *
 * 데모는 ERP 호출을 fetch 인터셉터로 가로채는 방식이지만, 이 파일은 Supabase 클라이언트를
 * 통해 다른 호스트로 나간다. 인터셉터는 ERP 호스트만 막으므로 여기는 따로 끊어 줘야
 * 데모에서 실제 마켓플레이스 데이터가 오가는 일이 없다.
 * 공개 견적 요청 탭은 데모에서 "빈 목록" 으로 보인다. (화면 라벨도 개발중이다)
 */
const demoBlocked = () => isDemoMode();

const API_BASE_URL = 'https://trpliveapi1.tripbox.co.kr';
const SP_SAVE = 'DBO.ITN_T_상담저장';

/* Tripbox 공통 시스템 코드 (hanmaeum estimateRequestService 와 동일) */
const CODE_상담구분 = '419001';
const CODE_상담상태 = '420001';
const CODE_통화 = '058001';
const SYMBOL_통화 = '￦';
const CODE_원가구분 = '401002';

const toNumber = (v) => {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  const n = Number(String(v ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};
const safeText = (v) => (v === null || v === undefined ? '' : String(v));

/**
 * 공개 견적 요청을 올린 대리점 표시 라벨.
 * 등록 시 저장된 업체명(content.agency_name)을 우선 쓰고, 없으면 번호로 폴백한다.
 * @param {{ agency_no?: number|string, content?: object }} request
 */
export const getAgencyLabel = (request) => {
  const name = safeText(request?.content?.agency_name).trim();
  if (name) return name;
  const no = toNumber(request?.agency_no);
  return no ? `대리점 #${no}` : '대리점 미상';
};

/** 대리점 업체번호 추출 */
export const getAgencyNo = (request) => toNumber(request?.agency_no);

// ── 대리점 업체 세부정보 (ITN_S_업체세부정보) ───────────────────────
const SP_COMPANY_DETAIL = 'DBO.ITN_S_업체세부정보';

/**
 * 대리점(업체) 세부정보 조회 — SP: DBO.ITN_S_업체세부정보
 * @param {number|string} companyNo 선택한 대리점의 업체번호
 * @returns {Promise<object|null>} table[0] 행 또는 null
 */
export const getAgencyCompanyDetail = async (companyNo) => {
  const no = toNumber(companyNo);
  if (!no) return null;

  const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=${encodeURIComponent(SP_COMPANY_DETAIL)}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
    body: JSON.stringify({
      Table1: [{ 컬럼명: '@업체번호', 값: no }],
    }),
  });
  if (!res.ok) throw new Error(`업체 세부정보 조회 실패: HTTP ${res.status}`);
  const data = await res.json();
  const rows = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
  return rows[0] ?? null;
};

// ── 공개 견적 요청 조회 ──────────────────────────────────────────────
/** status=open 인 공개 견적 요청 목록(최근순) */
export const listOpenRequests = async () => {
  if (demoBlocked()) return [];
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('open_estimate_requests')
    .select('*, bids:estimate_bids(*)')
    .eq('status', 'open')
    .order('created_at', { ascending: false });
  if (error) throw new Error(`공개 견적 요청 조회 실패: ${error.message}`);
  return data ?? [];
};

/** 단건 조회 */
export const getOpenRequest = async (id) => {
  if (demoBlocked()) return null;
  if (!supabase) return null;
  const { data, error } = await supabase
    .from('open_estimate_requests')
    .select('*, bids:estimate_bids(*)')
    .eq('id', id)
    .maybeSingle();
  if (error) throw new Error(`공개 견적 요청 조회 실패: ${error.message}`);
  return data ?? null;
};

/** 내가 이미 참여했는지 확인 */
export const getMyBid = async (requestId) => {
  if (demoBlocked()) return null;
  if (!supabase) return null;
  const { companyNo } = getUserInfo() || {};
  const { data, error } = await supabase
    .from('estimate_bids')
    .select('*')
    .eq('request_id', requestId)
    .eq('supplier_no', toNumber(companyNo))
    .maybeSingle();
  if (error) throw new Error(`응찰 조회 실패: ${error.message}`);
  return data ?? null;
};

/**
 * 특정 상담번호(counsel_no)에 걸린 내(로그인 공급사) 응찰 1건을 조회한다.
 * 상담상세 화면에서 낙찰/유찰 배지 + 요청 대리점을 띄우는 데 쓴다.
 * 요청(open_estimate_requests)을 FK 로 join 해 대리점 정보(agency_no/agency_name)도 함께 반환한다.
 * @returns { status, request_id, amount, memo, updated_at, agency_no, agency_name, agency_label } | null
 */
export const getMyBidByCounsel = async (counselNo) => {
  if (demoBlocked()) return null;
  if (!supabase) return null;
  const cn = toNumber(counselNo);
  const { companyNo } = getUserInfo() || {};
  const supplierNo = toNumber(companyNo);
  if (!cn || !supplierNo) return null;
  const { data, error } = await supabase
    .from('estimate_bids')
    .select('status, request_id, amount, memo, updated_at, request:open_estimate_requests(agency_no, content)')
    .eq('supplier_no', supplierNo)
    .eq('counsel_no', cn)
    .maybeSingle();
  if (error) throw new Error(`응찰 조회 실패: ${error.message}`);
  if (!data) return null;
  const req = data.request || null;
  return {
    status: data.status,
    request_id: data.request_id,
    amount: data.amount,
    memo: data.memo,
    updated_at: data.updated_at,
    agency_no: req?.agency_no ?? null,
    agency_name: req?.content?.agency_name ?? null,
    agency_label: req ? getAgencyLabel(req) : null,
  };
};

/**
 * 내(로그인 공급사) 응찰 전체를 상담번호(counsel_no) 기준으로 인덱싱해 돌려준다.
 * 판매사가 award_estimate_bid 로 낙찰하면 status 가 won/lost 로 바뀌므로,
 * 상담관리(MSSQL 상담리스트)의 각 상담행에 이 맵을 상담번호로 join 하면
 * SP 무수정으로 낙찰/유찰 상태를 보여줄 수 있다.
 * @returns { [counselNo:number]: { status, request_id, amount, updated_at } }
 */
export const getMyBidStatusByCounsel = async () => {
  if (demoBlocked()) return {};
  if (!supabase) return {};
  const { companyNo } = getUserInfo() || {};
  const supplierNo = toNumber(companyNo);
  if (!supplierNo) return {};
  const { data, error } = await supabase
    .from('estimate_bids')
    .select('counsel_no, status, request_id, amount, updated_at')
    .eq('supplier_no', supplierNo);
  if (error) throw new Error(`응찰 상태 조회 실패: ${error.message}`);
  const map = {};
  (data ?? []).forEach((bid) => {
    const cn = toNumber(bid.counsel_no);
    if (!cn) return; // 상담번호 복구 실패건은 매칭 불가 → 스킵
    map[cn] = {
      status: bid.status,
      request_id: bid.request_id,
      amount: bid.amount,
      updated_at: bid.updated_at,
    };
  });
  return map;
};

// ── ITN 상담 생성 (수배처=자기) ─────────────────────────────────
/**
 * 공개 견적 요청 내용으로 ITN 상담을 생성한다. hanmaeum estimateRequestService.saveEstimateRequest
 * 의 counselInfo 페이로드를 그대로 이식하되, 수배처번호를 참여 공급사(=나)로 지정한다.
 * @returns 생성된 상담번호(추출 실패 시 null)
 */
const createEstimateConsult = async (request) => {
  const { companyNo, userNo } = getUserInfo() || {};
  if (!companyNo) throw new Error('로그인 정보(업체번호)가 없습니다.');

  const c = request?.content || {};
  const r = request?.requester || {};
  const party = c.party || {};
  const price = c.price || {};
  const 성인수 = toNumber(party.성인수);
  const 소아수 = toNumber(party.소아수);
  const 유아수 = toNumber(party.유아수);

  const now = new Date();
  const localIso = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, -1);

  const counselInfo = {
    견적경로: '',
    상담번호: 0, // 신규
    상담구분: CODE_상담구분,
    상담상태: CODE_상담상태,
    조인업체명: '',
    담당자: safeText(c.담당자),
    담당자번호: toNumber(c.담당자번호), // 대리점 담당자(유저)번호
    판매업체: '',
    판매업체번호: 0,
    수배처: safeText(getUserInfo()?.companyName), // 표시명(있으면)
    수배처번호: toNumber(companyNo), // ★ 참여 공급사 = 나
    요청자명: safeText(r.요청자명),
    요청자휴대폰: safeText(r.요청자휴대폰),
    요청자이메일: safeText(r.요청자이메일),
    시작일: safeText(c.시작일),
    종료일: safeText(c.종료일),
    견적명: safeText(request?.title),
    견적내용: safeText(c.견적내용),
    고객명단: '',
    성인수,
    소아수,
    유아수,
    인솔자수: 0,
    성인판매가: toNumber(price.성인판매가),
    소아판매가: toNumber(price.소아판매가),
    유아판매가: toNumber(price.유아판매가),
    일정표주소: '',
    첨부파일리스트: [],
    견적첨부파일리스트: [],
    통화코드: CODE_통화,
    통화기호: SYMBOL_통화,
    원가구분: CODE_원가구분,
    총원가: [],
    공용원가: [],
    수익계산: [
      { 항목: '인원수', 번호: 1, 통화코드: CODE_통화, 통화기호: SYMBOL_통화, 성인: 성인수, 어린이: 소아수, 영유아: 유아수 },
      { 항목: '원가', 번호: 2, 통화코드: CODE_통화, 통화기호: SYMBOL_통화, 성인: 0, 어린이: 0, 영유아: 0 },
      { 항목: '판매가', 번호: 3, 통화코드: CODE_통화, 통화기호: SYMBOL_통화, 성인: toNumber(price.성인판매가), 어린이: toNumber(price.소아판매가), 영유아: toNumber(price.유아판매가) },
      { 항목: '예상수익', 번호: 4, 통화코드: CODE_통화, 통화기호: SYMBOL_통화, 성인: 0, 어린이: 0, 영유아: 0 },
    ],
    상담입출금: [
      { 상담번호: 0, 일시: localIso, 구분: '입금', 입출대상: '', 내역: '', 금액: 0 },
    ],
    고객번호: 0,
    일정번호: 0,
  };

  // 저장 @업체번호 = 요청을 올린 대리점(agency_no). 기존 flow 미러링.
  const agencyNo = toNumber(request?.agency_no);
  const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=${encodeURIComponent(SP_SAVE)}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
    body: JSON.stringify({
      Table1: [
        { 컬럼명: '@상담정보', 값: JSON.stringify(counselInfo) },
        { 컬럼명: '@처리자', 값: safeText(c.담당자번호 || userNo) },
        { 컬럼명: '@업체번호', 값: safeText(agencyNo || companyNo) },
        { 컬럼명: '@예상수익', 값: 0 },
        { 컬럼명: '@견적경로', 값: '' },
        { 컬럼명: '@성인원가', 값: 0 },
        { 컬럼명: '@소아원가', 값: 0 },
        { 컬럼명: '@유아원가', 값: 0 },
        { 컬럼명: '@입금총액', 값: 0 },
        { 컬럼명: '@출금총액', 값: 0 },
        { 컬럼명: '@총수익', 값: 0 },
      ],
    }),
  });
  if (!res.ok) throw new Error(`상담 저장 실패: HTTP ${res.status}`);
  const data = await res.json();

  // 1순위: 저장 응답에서 상담번호 추출.
  // 2순위: 응답이 번호를 안 돌려주면(ITN_T_상담저장은 종종 그렇다) 상담리스트에서
  //        방금 만든 상담을 찾아 번호를 복구한다(hanmaeum findRecentCounselNo 이식).
  let counselNo = extractCounselNo(data);
  if (!counselNo) {
    counselNo = await findRecentCounselNo({
      title: request?.title,
      requesterName: request?.requester?.요청자명,
    });
  }
  return counselNo;
};

/** YYYY-MM-DD (로컬) */
const ymd = (d) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/**
 * 저장 응답에 상담번호가 없을 때의 폴백 — 공급사 본인 상담리스트(상담관리와 동일)에서
 * 방금 만든 상담을 견적명(+요청자명)으로 찾아 가장 최근(큰) 상담번호를 택한다.
 * getConsultList 는 @업체번호=로그인 공급사 로 조회하므로, 수배처=공급사인 이 상담이 포함된다.
 */
const findRecentCounselNo = async ({ title, requesterName }) => {
  try {
    const now = new Date();
    const from = new Date(now.getTime() - 2 * 24 * 3600 * 1000);
    const to = new Date(now.getTime() + 1 * 24 * 3600 * 1000);
    const rows = await getConsultList({ startDate: ymd(from), endDate: ymd(to) });
    if (!Array.isArray(rows) || !rows.length) return null;
    const norm = (v) => String(v ?? '').trim();
    const t = norm(title);
    const rn = norm(requesterName);
    const byTitle = rows.filter((r) => norm(r.견적명) === t);
    const pool = rn ? byTitle.filter((r) => norm(r.요청자명) === rn) : byTitle;
    const cands = pool.length ? pool : byTitle;
    if (!cands.length) return null;
    const best = cands.reduce((a, b) =>
      toNumber(b.상담번호) > toNumber(a.상담번호) ? b : a,
    );
    const no = toNumber(best.상담번호);
    return no > 0 ? no : null;
  } catch {
    return null;
  }
};

/** 응답에서 생성된 상담번호 추출 (hanmaeum extractCounselNo 이식) */
const extractCounselNo = (res) => {
  const toNo = (v) => {
    if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v;
    if (typeof v === 'string') {
      const n = Number(v.replace(/,/g, '').trim());
      if (Number.isFinite(n) && n > 0) return n;
    }
    return null;
  };
  if (!res || typeof res !== 'object') return null;
  for (const k of ['상담번호', 'counselNo', 'CounselNo']) {
    const n = toNo(res[k]);
    if (n) return n;
  }
  for (const k of ['table', 'Table', 'table1', 'Table1']) {
    const t = res[k];
    if (Array.isArray(t) && t.length && t[0] && typeof t[0] === 'object') {
      const n = toNo(t[0].상담번호 ?? t[0].counselNo ?? t[0].CounselNo);
      if (n) return n;
    }
  }
  for (const v of Object.values(res)) {
    if (v && typeof v === 'object') {
      const nested = extractCounselNo(v);
      if (nested) return nested;
    }
  }
  return null;
};

// ── estimate_extras 로 일정 JSON 복사 ──────────────────────────
/** 참여로 생성된 상담번호에 요청의 일정/부가정보를 복사(상담 저장 시 상품 생성에 사용) */
const copyExtrasToConsult = async (counselNo, request) => {
  if (!supabase || !counselNo) return;
  const { companyNo } = getUserInfo() || {};
  const c = request?.content || {};
  const extras = {};
  if (c.destination) extras.destination = c.destination;
  if (c.city_no) extras.city_no = c.city_no;
  if (c.city_name) extras.city_name = c.city_name;
  if (c.itinerary) extras.itinerary = c.itinerary;
  if (c.jsonLineRoles) extras.jsonLineRoles = c.jsonLineRoles;
  if (c.itineraryHtml) extras.itineraryHtml = c.itineraryHtml;
  if (Object.keys(extras).length === 0) return;

  const { error } = await supabase.from('estimate_extras').upsert(
    {
      상담번호: toNumber(counselNo),
      업체번호: toNumber(companyNo),
      extras,
      updated_at: new Date().toISOString(),
    },
    { onConflict: '상담번호' },
  );
  if (error) console.warn('[openEstimate] extras 복사 실패(무시):', error.message);
};

// ── 참여(응찰) ─────────────────────────────────────────────────
/**
 * 공개 견적 요청에 참여한다:
 *   1) ITN 상담 생성(수배처=나) → 상담번호 확보 → 상담관리(/quotes) 이관
 *   2) estimate_bids 응찰 행 insert (중복 참여는 unique 제약으로 차단)
 *   3) 요청의 일정 JSON 을 estimate_extras 로 복사
 * @returns { counselNo, bid }
 */
export const participateOpenRequest = async (request, { amount, memo } = {}) => {
  if (demoBlocked()) throw new Error('데모 버전에서는 공개 견적 참여를 사용할 수 없습니다.');
  if (!supabase) throw new Error('Supabase 가 설정되지 않았습니다.');
  const { companyNo } = getUserInfo() || {};
  if (!companyNo) throw new Error('로그인 정보(업체번호)가 없습니다.');

  // 1) 상담 생성
  const counselNo = await createEstimateConsult(request);

  // 2) 응찰 insert
  const { data: bid, error } = await supabase
    .from('estimate_bids')
    .insert({
      request_id: request.id,
      supplier_no: toNumber(companyNo),
      counsel_no: counselNo,
      amount: amount != null ? toNumber(amount) : null,
      memo: memo || null,
      status: 'submitted',
    })
    .select('*')
    .single();
  if (error) {
    // unique 위반(이미 참여) 등
    throw new Error(`참여 실패: ${error.message}`);
  }

  // 3) 일정/부가정보 복사(실패해도 참여 자체는 유지)
  await copyExtrasToConsult(counselNo, request);

  return { counselNo, bid };
};
